// components/ConversionQueue.jsx
// Created: 2026-03-11 15:00:00 EST
// Updated: 2026-03-11 16:30:00 EST
// Project: Giving Words to Voices
// Author: Michael Schwarz
// Version: 1.0.0
//
// Video conversion tracker.
// Self-contained component with its own polling loop.
// Uses direct DOM writes for progress % so the main App never re-renders.

import React from 'react';
import { ChevronUp, Check, AlertTriangle, Clock, X, Loader } from 'lucide-react';
import { Icon } from './Icon';
import Button from './ui/Button';
import { useConfirm } from '../confirmContext';
import { Z } from '../constants/tokens';

const ConversionQueue = React.memo(function ConversionQueue({ BACKEND_URL, theme, isDarkMode, onJobDone }) {
  const [jobs, setJobs]         = React.useState({});
  const [expanded, setExpanded] = React.useState(true);
  const intervalsRef            = React.useRef({});
  const lastStateRef            = React.useRef({});   // { rel_path: { status, progress } }
  const confirm                 = useConfirm();

  const TERMINAL = new Set(['done', 'error', 'cancelled']);

  ConversionQueue.addJob = (rel_path) => {
    setJobs(prev => ({ ...prev, [rel_path]: { status: 'queued', progress: 0, message: 'Queued...' } }));
    startPolling(rel_path);
  };
  ConversionQueue.clearJob = (rel_path) => {
    if (intervalsRef.current[rel_path]) {
      clearInterval(intervalsRef.current[rel_path]);
      delete intervalsRef.current[rel_path];
    }
    delete lastStateRef.current[rel_path];
    setJobs(prev => { const n = {...prev}; delete n[rel_path]; return n; });
  };
  ConversionQueue.hasJob = (rel_path) => !!lastStateRef.current[rel_path];

  const startPolling = (rel_path) => {
    if (intervalsRef.current[rel_path]) clearInterval(intervalsRef.current[rel_path]);

    // Consecutive-failure counter: a single network blip must not kill polling
    // and strand the job at "Converting" forever. Only give up after several
    // failures in a row, and then say so instead of freezing the progress bar.
    let failures = 0;
    const MAX_FAILURES = 5;

    const tick = () => {
      fetch(`${BACKEND_URL}/convert/status?rel_path=${encodeURIComponent(rel_path)}`)
        .then(r => {
          if (!r.ok) throw new Error(`status ${r.status}`);
          return r.json();
        })
        .then(data => {
          failures = 0;
          const last = lastStateRef.current[rel_path] || {};

          const escapedId = rel_path.replace(/[^a-zA-Z0-9-_]/g, '_');
          const barEl = document.getElementById(`cq-bar-${escapedId}`);
          const pctEl = document.getElementById(`cq-pct-${escapedId}`);
          if (barEl) barEl.style.width = `${data.progress || 0}%`;
          if (pctEl) pctEl.textContent = `${data.progress || 0}%`;

          if (TERMINAL.has(data.status)) {
            clearInterval(intervalsRef.current[rel_path]);
            delete intervalsRef.current[rel_path];
          }

          if (last.status !== data.status) {
            lastStateRef.current[rel_path] = { status: data.status, progress: data.progress };
            setJobs(prev => ({ ...prev, [rel_path]: data }));
            if (data.status === 'done' && onJobDone) onJobDone(rel_path);
          } else {
            lastStateRef.current[rel_path] = { ...last, progress: data.progress };
          }
        })
        .catch(() => {
          failures += 1;
          if (failures < MAX_FAILURES) return;   // transient — keep polling
          clearInterval(intervalsRef.current[rel_path]);
          delete intervalsRef.current[rel_path];
          // Reflect reality rather than leaving a stuck "Converting" bar.
          const last = lastStateRef.current[rel_path] || {};
          lastStateRef.current[rel_path] = { ...last, status: 'error' };
          setJobs(prev => ({
            ...prev,
            [rel_path]: {
              ...(prev[rel_path] || {}),
              status: 'error',
              message: 'Lost connection to the conversion service',
            },
          }));
        });
    };

    lastStateRef.current[rel_path] = { status: 'queued', progress: 0 };
    tick();
    intervalsRef.current[rel_path] = setInterval(tick, 3000);
  };

  React.useEffect(() => {
    return () => Object.values(intervalsRef.current).forEach(clearInterval);
  }, []);

  if (Object.keys(jobs).length === 0) return null;

  const activeCount    = Object.values(jobs).filter(j => j.status === 'running' || j.status === 'queued').length;
  const doneCount      = Object.values(jobs).filter(j => j.status === 'done').length;
  const errorCount     = Object.values(jobs).filter(j => j.status === 'error').length;
  const cancelledCount = Object.values(jobs).filter(j => j.status === 'cancelled').length;
  const finishedCount  = doneCount + errorCount + cancelledCount;

  const clearFinished = (ev) => {
    ev.stopPropagation();
    Object.entries(jobs).forEach(([path, job]) => {
      if (TERMINAL.has(job.status)) ConversionQueue.clearJob(path);
    });
  };

  // Clearing only stops tracking — it does not cancel anything server-side. The
  // per-item X is deliberately hidden for active jobs, so warn before this bulk
  // path drops in-flight conversions the user can no longer follow.
  const clearAll = async (ev) => {
    ev.stopPropagation();
    if (activeCount > 0) {
      const ok = await confirm({
        title: 'Stop tracking active conversions?',
        message: `${activeCount} conversion${activeCount !== 1 ? 's are' : ' is'} still in progress. Clearing the queue removes ${activeCount !== 1 ? 'them' : 'it'} from this list but does not cancel ${activeCount !== 1 ? 'them' : 'it'} — ${activeCount !== 1 ? 'they' : 'it'} will keep running on the server and you will no longer see when ${activeCount !== 1 ? 'they finish' : 'it finishes'}.`,
        confirmLabel: 'Clear queue',
        destructive: true,
      });
      if (!ok) return;
    }
    Object.keys(jobs).forEach(path => ConversionQueue.clearJob(path));
  };

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: Z.toast,
      background: isDarkMode ? '#1c1c1e' : '#ffffff',
      borderTop: `1px solid ${theme.border}`,
      boxShadow: '0 -6px 30px rgba(0,0,0,0.18)',
    }}>
      <style>{`
        @keyframes cq-pulse { 0%,100%{opacity:1;transform:scale(1);}50%{opacity:0.5;transform:scale(1.3);} }
      `}</style>

      {/* Header */}
      <div
        onClick={() => setExpanded(e => !e)}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 20px', cursor: 'pointer', borderBottom: expanded ? `1px solid ${theme.border}` : 'none', userSelect: 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
            background: errorCount > 0 ? theme.accent : activeCount > 0 ? (theme.success || '#34c759') : theme.card,
            boxShadow: 'none',
            animation: activeCount > 0 ? 'cq-pulse 1.4s ease-in-out infinite' : 'none',
          }} />
          <span style={{ fontWeight: 'bold', fontSize: '0.78rem', letterSpacing: '0.05em' }}>CONVERSION QUEUE</span>
          <span style={{ fontSize: '0.72rem', color: theme.textSub }}>
            {activeCount > 0 && <span style={{ color: theme.textSub, fontWeight: 'bold' }}>{activeCount} active</span>}
            {activeCount > 0 && finishedCount > 0 && <span> · </span>}
            {doneCount      > 0 && <span style={{ color: theme.textSub }}>{doneCount} done</span>}
            {doneCount      > 0 && (errorCount > 0 || cancelledCount > 0) && <span> · </span>}
            {errorCount     > 0 && <span style={{ color: theme.accent }}>{errorCount} failed</span>}
            {errorCount     > 0 && cancelledCount > 0 && <span> · </span>}
            {cancelledCount > 0 && <span style={{ color: theme.textSub }}>{cancelledCount} cancelled</span>}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {finishedCount > 0 && (
            <Button theme={theme} variant="secondary" size="sm" onClick={clearFinished}>Clear finished</Button>
          )}
          <Button theme={theme} variant="secondary" size="sm" onClick={clearAll}>Clear queue</Button>
          {/* The chevron is the keyboard-operable toggle. The header strip keeps
              its onClick for mouse convenience, but it must NOT become a
              role="button" itself: it already contains the two Clear buttons,
              and a keydown handler on the container would also fire when Enter
              is pressed on those, clearing AND toggling in one keystroke. */}
          <button
            type="button"
            onClick={(ev) => { ev.stopPropagation(); setExpanded(e => !e); }}
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse conversion queue' : 'Expand conversion queue'}
            title={expanded ? 'Collapse conversion queue' : 'Expand conversion queue'}
            style={{ background: 'none', border: 'none', color: theme.textSub, fontSize: '0.8rem', cursor: 'pointer', padding: '4px 6px', margin: '-4px -6px', display: 'inline-flex', alignItems: 'center', transition: 'transform 0.2s', transform: expanded ? 'rotate(0deg)' : 'rotate(180deg)' }}
          >
            <Icon icon={ChevronUp} size={14} />
          </button>
        </div>
      </div>

      {/* Job list */}
      {expanded && (
        <div style={{ overflowY: 'auto', maxHeight: '220px', padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {Object.entries(jobs).map(([rel_path, job]) => {
            const filename    = rel_path.split('/').pop().replace(/\.mkv$/i, '');
            const isRunning   = job.status === 'running';
            const isQueued    = job.status === 'queued';
            const isActive    = isRunning || isQueued;
            const isDone      = job.status === 'done';
            const isError     = job.status === 'error';
            const isCancelled = job.status === 'cancelled';
            const pct         = job.progress || 0;
            const trackColor  = isError ? theme.accent : theme.textSub;
            const statusLabel = isDone      ? <><Icon icon={Check}         size={12} style={{marginRight:'4px'}} />Done</>
                              : isError     ? <><Icon icon={AlertTriangle} size={12} style={{marginRight:'4px'}} />Failed</>
                              : isQueued    ? <><Icon icon={Clock}         size={12} style={{marginRight:'4px'}} />Queued</>
                              : isCancelled ? <><Icon icon={X}             size={12} style={{marginRight:'4px'}} />Cancelled</>
                              :               <><Icon icon={Loader}        size={12} style={{marginRight:'4px'}} />Converting</>;
            const escapedId   = rel_path.replace(/[^a-zA-Z0-9-_]/g, '_');

            return (
              <div key={rel_path}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 'bold', color: trackColor, flexShrink: 0, minWidth: '72px' }}>{statusLabel}</span>
                  <span style={{ fontSize: '0.82rem', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, color: isError ? theme.accent : theme.text }}>{filename}</span>
                  <span style={{ fontSize: '0.7rem', color: theme.textSub, flexShrink: 0, maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.message}</span>
                  {isActive  && <span id={`cq-pct-${escapedId}`} style={{ fontSize: '0.75rem', fontWeight: 'bold', color: trackColor, minWidth: '34px', textAlign: 'right', flexShrink: 0 }}>{pct}%</span>}
                  {!isActive && <button onClick={() => ConversionQueue.clearJob(rel_path)} aria-label={`Remove ${filename} from queue`} title={`Remove ${filename} from queue`} style={{ background: 'none', border: 'none', color: theme.textSub, cursor: 'pointer', display:'flex', alignItems:'center', flexShrink: 0 }}><Icon icon={X} size={14} /></button>}
                </div>
                <div style={{ height: '6px', background: theme.card, borderRadius: '4px', overflow: 'hidden' }}>
                  <div
                    id={`cq-bar-${escapedId}`}
                    style={{
                      height: '100%', width: `${pct}%`, borderRadius: '4px',
                      transition: isRunning ? 'width 2s ease' : 'none',
                      background: isError     ? theme.accent
                                : isDone      ? theme.textSub
                                : isCancelled ? theme.border
                                : isQueued    ? `repeating-linear-gradient(90deg,${theme.card} 0px,${theme.card} 8px,${theme.border} 8px,${theme.border} 16px)`
                                : theme.textSub,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

export default ConversionQueue;
